// ═══════════════════════════════════════════════════════════════════════════════
// Breadcrumb — clickable trail used by the KB folder + article views.
//
// - Last item is rendered as a bold, non-clickable span with aria-current="page".
// - Preceding items are <button> elements; clicking invokes their onClick.
// - When the container is narrower than COLLAPSE_PX and there are more than
//   COLLAPSE_THRESHOLD segments, the middle segments collapse to a single
//   "…" item that becomes a tooltip listing the hidden labels.
// - The outer <nav> has aria-label="Breadcrumb".
// ═══════════════════════════════════════════════════════════════════════════════
import { useEffect, useRef, useState } from 'react';
import { theme } from '../../lib/theme';

export interface BreadcrumbItem {
  label: string;
  onClick?: () => void;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  style?: React.CSSProperties;
}

const COLLAPSE_PX = 640;
const COLLAPSE_THRESHOLD = 3;

export function Breadcrumb({ items, style }: BreadcrumbProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(false);

  // Observe container width — collapse only when narrow AND we have more than
  // 3 segments. We re-evaluate on every resize.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        setCollapsed(w < COLLAPSE_PX && items.length > COLLAPSE_THRESHOLD);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [items.length]);

  if (!items.length) return null;

  // Compute which segments to render. When collapsed, we keep the first item,
  // a single "…" placeholder (whose title attribute lists the hidden labels),
  // and the last two items (penultimate + current).
  const visible: Array<BreadcrumbItem | { ellipsis: true; hiddenLabels: string[] }> =
    collapsed && items.length > COLLAPSE_THRESHOLD
      ? [
          items[0],
          { ellipsis: true, hiddenLabels: items.slice(1, items.length - 2).map(i => i.label) },
          ...items.slice(items.length - 2),
        ]
      : items;

  return (
    <nav
      ref={ref as any}
      aria-label="Breadcrumb"
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 6,
        fontSize: 12,
        color: theme.textSecondary,
        ...style,
      }}
    >
      {visible.map((seg, i) => {
        const isLastVisible = i === visible.length - 1;
        const isLastRealItem = isLastVisible && !('ellipsis' in seg);

        // Separator between segments (skip before the first)
        const sep = i > 0 ? (
          <span aria-hidden="true" style={{ color: theme.textTertiary, fontSize: 12 }}>›</span>
        ) : null;

        if ('ellipsis' in seg) {
          return (
            <span key={`ell-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {sep}
              <span
                title={seg.hiddenLabels.join(' › ')}
                aria-label={`${seg.hiddenLabels.length} more`}
                style={{
                  color: theme.textTertiary,
                  fontWeight: 500,
                  padding: '0 4px',
                  cursor: 'help',
                }}
              >
                …
              </span>
            </span>
          );
        }

        if (isLastRealItem) {
          return (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {sep}
              <span aria-current="page" style={{ color: theme.text, fontWeight: 600 }}>
                {seg.label}
              </span>
            </span>
          );
        }

        // Clickable middle/leading item
        return (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {sep}
            <button
              type="button"
              onClick={seg.onClick}
              style={{
                background: 'none',
                border: 'none',
                color: theme.accent,
                cursor: seg.onClick ? 'pointer' : 'default',
                fontSize: 12,
                fontFamily: 'inherit',
                padding: 0,
                fontWeight: 500,
              }}
            >
              {seg.label}
            </button>
          </span>
        );
      })}
    </nav>
  );
}
